import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';
import { OrgMembership, OrgRole, ProjectAccessRole } from '@app-types/access';

export interface Organization {
  _id?: string; // Mongo id (may be present)
  orgId: string; // UUID used by backend membership checks
  name: string;
  slug: string;
  description?: string;
  status?: 'active' | 'deleting';
  createdAt?: string;
  // Optional summary fields returned by the org-list response. Safe to read
  // before the backend deploys them; the card degrades gracefully when absent.
  projectsCount?: number;
  membersCount?: number;
  plan?: string; // effective plan identifier, e.g. 'free' | 'pro'
  role?: OrgRole; // caller's role in the org (org-list response)
  isOwner?: boolean; // whether the caller owns (created) the org
}

export interface OrganizationMember {
  userId: string;
  role: OrgRole;
  isOwner?: boolean;
  isCurrentUser?: boolean;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    isDeleted?: boolean;
  };
}

export interface OrganizationInviteSender {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isDeleted?: boolean;
}

export interface OrganizationPendingInviteProjectAccess {
  projectId: string;
  role: ProjectAccessRole;
  projectName?: string;
  projectSlug?: string;
}

export interface OrganizationPendingInvitation {
  inviteId: string;
  inviteType: 'organization';
  email: string;
  role: OrgRole;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  invitedBy: OrganizationInviteSender | null;
  projectAccesses: OrganizationPendingInviteProjectAccess[];
}

export interface OrganizationMemberListResponse {
  items: OrganizationMember[];
  nextCursor: string | null;
  hasMore: boolean;
  pendingInvitations: OrganizationPendingInvitation[];
}

export interface GetOrganizationMembersRequest {
  orgId: string;
  limit?: number;
  cursor?: string;
}

export interface SearchOrganizationMembersRequest {
  orgId: string;
  query?: string;
  name?: string;
  limit?: number;
  cursor?: string;
}

export interface OrgInviteProject {
  projectId: string;
  role?: ProjectAccessRole;
}

export interface InviteOrganizationMemberRequest {
  orgId: string;
  email: string;
  role: OrgRole;
  projects?: OrgInviteProject[];
  projectIds?: string[];
}

export interface OrganizationDeleteResponse {
  orgId: string;
  status: 'deleting';
  cleanup: {
    mode: 'queued';
    jobId: string | null;
  };
}

export interface OrganizationCleanupJobStatusResponse {
  jobId: string;
  state: string;
  isDone: boolean;
  isSuccess: boolean;
  data: {
    orgId: string;
    requestedByUserId: string;
    requestedAt: string;
  };
  attemptsMade: number;
  failedReason: string | null;
  result: unknown;
  timestamp: number | null;
  processedOn: number | null;
  finishedOn: number | null;
  organizationStatus: 'active' | 'deleting' | 'deleted';
}

export interface OrganizationTransferUser {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isDeleted?: boolean;
}

export interface OrganizationTransfer {
  transferId: string;
  orgId: string;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  organization: {
    orgId: string;
    name: string;
    slug: string;
  } | null;
  toUser: OrganizationTransferUser | null;
  initiatedBy: OrganizationTransferUser | null;
}

export interface ActiveOrganizationTransferResponse {
  transfer: OrganizationTransfer | null;
}

export const normalizeOrganizationMemberListResponse = (
  response: unknown
): OrganizationMemberListResponse => {
  if (Array.isArray(response)) {
    return {
      items: response as OrganizationMember[],
      nextCursor: null,
      hasMore: false,
      pendingInvitations: [],
    };
  }

  const typed = response as Partial<OrganizationMemberListResponse> | null | undefined;
  return {
    items: Array.isArray(typed?.items) ? typed.items : [],
    nextCursor: typed?.nextCursor ?? null,
    hasMore: Boolean(typed?.hasMore),
    pendingInvitations: Array.isArray(typed?.pendingInvitations)
      ? (typed.pendingInvitations as OrganizationPendingInvitation[])
      : [],
  };
};

export const getMyOrganizationsTags = (result?: Organization[]) =>
  result
    ? [
        ...result.map(org => ({ type: 'Organizations' as const, id: org.orgId })),
        { type: 'Organizations' as const, id: 'LIST' },
      ]
    : [{ type: 'Organizations' as const, id: 'LIST' }];

export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Organizations', 'OrganizationMembers', 'OrganizationTransfers'],
  endpoints: builder => ({
    getMyOrganizations: builder.query<Organization[], void>({
      query: () => ({ url: '/organizations/mine' }),
      providesTags: result => getMyOrganizationsTags(result),
    }),
    createOrganization: builder.mutation<
      { organization: Organization; membership: OrgMembership },
      { name: string; slug: string; description?: string }
    >({
      query: body => ({
        url: '/organizations/create',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
      invalidatesTags: [{ type: 'Organizations', id: 'LIST' }],
    }),
    deleteOrganization: builder.mutation<OrganizationDeleteResponse, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/organizations/${orgId}`,
        method: 'DELETE',
        timeout: 10_000,
        timeoutErrorMessage:
          'Organization deletion request timed out. Refresh organizations to check whether cleanup started, then try again if it is still active.',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'Organizations', id: orgId },
        { type: 'Organizations', id: 'LIST' },
      ],
    }),
    getOrganizationCleanupStatus: builder.query<OrganizationCleanupJobStatusResponse, string>({
      query: jobId => ({
        url: `/organizations/cleanup-jobs/${jobId}`,
      }),
    }),
    initiateOrganizationTransfer: builder.mutation<
      OrganizationTransfer,
      { orgId: string; receiverEmail: string }
    >({
      query: ({ orgId, receiverEmail }) => ({
        url: `/organizations/${orgId}/transfers`,
        method: 'POST',
        data: { receiverEmail },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationTransfers', id: orgId },
      ],
    }),
    getActiveOrganizationTransfer: builder.query<ActiveOrganizationTransferResponse, string>({
      query: orgId => ({
        url: `/organizations/${orgId}/transfers/active`,
      }),
      providesTags: (_result, _error, orgId) => [{ type: 'OrganizationTransfers', id: orgId }],
    }),
    cancelOrganizationTransfer: builder.mutation<
      { transferId: string; orgId: string; status: 'canceled' },
      { orgId: string; transferId: string }
    >({
      query: ({ orgId, transferId }) => ({
        url: `/organizations/${orgId}/transfers/${transferId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationTransfers', id: orgId },
      ],
    }),
    getOrganizationBySlug: builder.query<Organization, string>({
      query: slug => ({ url: `/organizations/${slug}` }),
      providesTags: (_result, _error, slug) => [{ type: 'Organizations', id: slug }],
    }),
    getOrganizationMembers: builder.query<
      OrganizationMemberListResponse,
      GetOrganizationMembersRequest
    >({
      query: ({ orgId, limit, cursor }) => ({
        url: `/organizations/${orgId}/members`,
        params: { limit, cursor },
      }),
      transformResponse: response => normalizeOrganizationMemberListResponse(response),
      providesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationMembers', id: orgId },
        { type: 'OrganizationMembers', id: 'LIST' },
      ],
    }),
    searchOrganizationMembers: builder.query<
      OrganizationMemberListResponse,
      SearchOrganizationMembersRequest
    >({
      query: ({ orgId, query, name, limit, cursor }) => ({
        url: `/organizations/${orgId}/members/search`,
        params: { query, name, limit, cursor },
      }),
      transformResponse: response => normalizeOrganizationMemberListResponse(response),
      providesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationMembers', id: orgId },
        { type: 'OrganizationMembers', id: 'LIST' },
      ],
    }),
    inviteOrganizationMember: builder.mutation<{ message?: string }, InviteOrganizationMemberRequest>({
      query: ({ orgId, ...body }) => ({
        url: `/organizations/${orgId}/members/invite`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationMembers', id: orgId },
        { type: 'OrganizationMembers', id: 'LIST' },
      ],
    }),
    updateOrganizationMemberRole: builder.mutation<
      { message?: string },
      { orgId: string; userId: string; role: OrgRole }
    >({
      query: ({ orgId, userId, role }) => ({
        url: `/organizations/${orgId}/members/${userId}`,
        method: 'PATCH',
        data: { role },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationMembers', id: orgId },
        { type: 'OrganizationMembers', id: 'LIST' },
      ],
    }),
    revokeOrganizationMember: builder.mutation<{ message?: string }, { orgId: string; userId: string }>({
      query: ({ orgId, userId }) => ({
        url: `/organizations/${orgId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationMembers', id: orgId },
        { type: 'OrganizationMembers', id: 'LIST' },
      ],
    }),
    cancelOrganizationInvitation: builder.mutation<
      { inviteId: string; status: string },
      { orgId: string; inviteId: string }
    >({
      query: ({ orgId, inviteId }) => ({
        url: `/organizations/${orgId}/members/invites/${inviteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrganizationMembers', id: orgId },
        { type: 'OrganizationMembers', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetMyOrganizationsQuery,
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useLazyGetOrganizationCleanupStatusQuery,
  useInitiateOrganizationTransferMutation,
  useGetActiveOrganizationTransferQuery,
  useCancelOrganizationTransferMutation,
  useGetOrganizationBySlugQuery,
  useGetOrganizationMembersQuery,
  useSearchOrganizationMembersQuery,
  useInviteOrganizationMemberMutation,
  useUpdateOrganizationMemberRoleMutation,
  useRevokeOrganizationMemberMutation,
  useCancelOrganizationInvitationMutation,
} = organizationsApi;
